import {
  Box,
  Button,
  Card,
  CardContent,
  duration,
  Grid,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import StatCard from './StatCard'
import {
  getWorkoutTitle,
  mapPunchesToTargets,
  computeStats,
  PB_BASE,
  randomForce,
  randomBpm,
  enrichWorkoutNew,
  fmtDate,
  startWorkout,
  pauseWorkout,
  finishWorkout,
  resumeWorkout
} from './util'
import WaveformTrack from './WaveformTrack'
import MetricChart from './MetricChart'
import { useState, useEffect, useRef } from 'react'

import { pb, useRecord } from '@/pb'
import * as api from '../../api'

export default function WorkoutView({ workoutid }) {
  const viewingWorkout = useRecord('music_workouts', workoutid)
  const viewingWorkoutGroup = useRecord('workout_groups', viewingWorkout?.groupId || '')
  const workoutIsInGroup = !!viewingWorkout?.groupId;
  const state = workoutIsInGroup ? viewingWorkoutGroup?.state || "ended" : viewingWorkout?.state || "ended"
  const workoutActive = state === "playing" || state === "paused"
  const workoutPlaying = state === "playing"
  const workoutStartTime = workoutIsInGroup ? viewingWorkoutGroup?.startTime : viewingWorkout?.startTime

  const workoutDevice = useRecord('devices', viewingWorkout?.deviceId || '')
  const workoutTemplate = useRecord('music_workout_templates', viewingWorkout?.templateId || '', { expand: 'musicId' })
  const enrichedWorkout = enrichWorkoutNew(viewingWorkout, workoutTemplate)

  const [playing, setPlaying] = useState(false)
  const [activePlayer, setActivePlayer] = useState(null)

  const punches = enrichedWorkout?.punchTimestamps || viewingWorkout?.punchTimestamps || []
  const forces = enrichedWorkout?.punchForces || viewingWorkout?.punchForces || []
  const heartrateTimestamps = enrichedWorkout?.heartrateTimestamps || viewingWorkout?.heartrateTimestamps || []
  const heartrateBpms = enrichedWorkout?.heartrateBpms || viewingWorkout?.heartrateBpms || []

  const targets = workoutTemplate?.punchTargets || []
  const targetsMap = mapPunchesToTargets(punches, targets)

  const stats = computeStats(targetsMap, targets, punches)

  const avgForceCompute = forces.length
    ? Math.round(forces.reduce((a, b) => a + b, 0) / forces.length)
    : 0

  const displayEfficiency = (!workoutActive && viewingWorkout?.efficiency !== undefined)
    ? `${viewingWorkout.efficiency}%`
    : stats ? `${stats.efficiency.toFixed(0)}%` : '0%'

  const displayAvgForce = (!workoutActive && viewingWorkout?.avgForce !== undefined)
    ? viewingWorkout.avgForce
    : avgForceCompute

  const displayAvgDelay = (!workoutActive && viewingWorkout?.averageDelay !== undefined)
    ? `${viewingWorkout.averageDelay}с`
    : stats ? `${stats.avgDelay.toFixed(2)}с` : '0с'

  // NEW STATE EFFECT

  useEffect(() => {
    if (!activePlayer) return;

    if (workoutStartTime !== 0) {
      let passedTime = (Date.now() - workoutStartTime) / 1000
      console.log("TIME SET", workoutStartTime, passedTime)
      activePlayer?.setTime(passedTime)
    }

    setPlaying(workoutPlaying)
  }, [activePlayer, workoutPlaying, workoutStartTime]);

  useEffect(() => {
    if (!activePlayer) return;
    if (playing) activePlayer?.play();
    else activePlayer?.pause();
  }, [activePlayer, playing]);

  const startWorkoutManually = async () => {
    if (!!viewingWorkout?.lastPauseTime) {
      try {
        const resumeTime = Date.now();

        await resumeWorkout({
          workoutId: workoutid,
          resumeTime: resumeTime,
        });

        setPlaying(true);
      } catch (error) {
        console.error("Failed to resume workout:", error);
      }
    } else {
      try {
        const startTime = Date.now() + api.serverTimeOffset;

        await startWorkout({
          workoutId: workoutid,
          startTime,
        });

        setPlaying(true);
      } catch (error) {
        console.error("Failed to start workout:", error);
      }
    }
  };

  const pauseWorkoutManually = async () => {
    try {
      const pauseTime = Date.now();
      console.log("PAUSED", viewingWorkout?.lastPauseTime, !!viewingWorkout?.lastPauseTime, pauseTime)

      await pauseWorkout({
        workoutId: workoutid,
        lastPauseTime: pauseTime
      });

      setPlaying(false);
    } catch (error) {
      console.error("Failed to pause workout:", error);
    }
  };

  const handlePunch = async () => {
    if (!workoutTemplate || !workoutDevice) return;

    const force = randomForce();
    const bpm = randomBpm();
    const unixtime_ms = Date.now();
    const deviceId = workoutDevice.deviceId;
    const token = pb.authStore.token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      await fetch(`${PB_BASE}/api/device/${deviceId}/punch/${unixtime_ms}/${force}`, { method: 'GET', headers });
      await fetch(`${PB_BASE}/api/device/${deviceId}/heartrate/${unixtime_ms}/${bpm}`, { method: 'GET', headers });
    } catch (err) {
      console.error('Punch API error:', err);
    }
  };

  const onFinish = async () => {
    try {
      await finishWorkout({
        workoutId: viewingWorkout.id,
      });
    } catch (error) {
      console.error("Failed to finish workout:", error);
    }
  }

  const finishBtnRef = useRef(null);


  const dumbestSolutionEver = () => {
    finishBtnRef.current?.click()
  }

  if (!viewingWorkout) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
        {workoutActive ? 'Тренировка' : 'Предыдущая тренировка'}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {workoutActive
          ? (workoutTemplate ? getWorkoutTitle(workoutTemplate) : 'No workout selected')
          : fmtDate(viewingWorkout.created)
        }
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <WaveformTrack
            workout={workoutActive ? workoutTemplate : (viewingWorkout?.template || workoutTemplate || {})}
            punches={punches}
            playing={workoutActive ? playing : false}
            onPlayingChange={workoutActive ? setPlaying : undefined}
            onReady={workoutActive ? setActivePlayer : undefined}
            onTrackFinished={dumbestSolutionEver}
            autoPlay={workoutActive}
            readOnly={!workoutActive}
          />
          {workoutActive && !viewingWorkout?.groupId && (
            <Button
              variant="contained"
              onClick={workoutPlaying ? pauseWorkoutManually : startWorkoutManually}
              sx={{ mt: 1 }}
            >
              {workoutPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </Button>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 1.5 }}>
        <MetricChart timestamps={punches} vals={forces} title='Сила удара' />
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <MetricChart timestamps={heartrateTimestamps} vals={heartrateBpms} title='Пульс сердца' />
      </Box>

      <Grid
        container
        spacing={1.5}
        sx={{
          mt: 0.5,
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Grid item xs={4}>
          <StatCard label="Эффективность" value={displayEfficiency} />
        </Grid>

        <Grid item xs={4}>
          <StatCard label="Средняя сила" value={displayAvgForce} />
        </Grid>

        <Grid item xs={4}>
          <StatCard label="Среднее отставание" value={displayAvgDelay} />
        </Grid>
      </Grid>

      {workoutActive && (
        <>
          <Button fullWidth variant="contained" sx={{ mt: 1.5 }} onClick={handlePunch}>
            Симулировать удар
          </Button>

          <Button
            ref={finishBtnRef}
            fullWidth
            variant="outlined"
            sx={{ mt: 1.5 }}
            onClick={onFinish}
          >
            Закончить
          </Button>
        </>
      )}
    </Box>
  )
}